import React from 'react';
import { Target, Repeat } from 'lucide-react';
import { GoalRow, SectionTitle } from './PlannerBits';
import { DOW_HEADERS } from './plannerUtils';

const LEGEND = [
  { key: 'tasks', label: 'Tasks', cls: 'bg-[color:var(--p-text-soft)]' },
  { key: 'events', label: 'Events', cls: 'bg-[color:var(--p-accent)]' },
  { key: 'meetings', label: 'Meetings', cls: 'bg-[color:var(--p-accent-2)]' },
  { key: 'goals', label: 'Goals', cls: 'bg-[color:var(--p-success)]' },
  { key: 'notes', label: 'Notes', cls: 'bg-[color:var(--p-muted)]' },
];

// Shared column template: a slim week-number gutter + 7 equal day columns.
const GRID_COLS = 'grid grid-cols-[2.25rem_repeat(7,minmax(0,1fr))] gap-2';

const Dot = ({ cls, n }) => (
  <span className="inline-flex items-center gap-0.5">
    <span className={`h-1.5 w-1.5 rounded-full ${cls}`} />
    {n > 1 && <span className="p-tnum text-[9px] font-semibold text-[color:var(--p-muted)]">{n}</span>}
  </span>
);

export default function MonthlyPlannerView({ data, onOpenDate, onOpenWeek, onToggleGoal, onQuantityGoal, onEditGoal }) {
  const weeks = data.weeks || [];
  const monthlyGoals = data.monthly_goals || [];

  return (
    <div className="p-fade-in mx-auto max-w-6xl space-y-4">
      {monthlyGoals.length > 0 && (
        <div className="rounded-[var(--p-radius-lg)] border border-[color:var(--p-border)] bg-[color:var(--p-card)] p-4 shadow-[var(--p-shadow)]" data-testid="planner-monthly-goals-panel">
          <SectionTitle icon={Target}>Monthly goals</SectionTitle>
          <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-2 lg:grid-cols-3">
            {monthlyGoals.map((g) => <GoalRow key={g.id} goal={g} onToggle={onToggleGoal} onQuantity={onQuantityGoal} onEdit={onEditGoal} compact />)}
          </div>
        </div>
      )}

      <div className="rounded-[var(--p-radius-lg)] border border-[color:var(--p-border)] bg-[color:var(--p-card)] p-3 shadow-[var(--p-shadow)] sm:p-4" data-testid="planner-monthly-view">
        {/* Header row: empty gutter + weekday names */}
        <div className={`mb-2 ${GRID_COLS}`}>
          <div className="flex items-center justify-center text-[10px] font-semibold uppercase tracking-wider text-[color:var(--p-muted-2)]" title="Week number (weeks start Sunday)">Wk</div>
          {DOW_HEADERS.map((d) => <div key={d} className="px-1 text-center text-[11px] font-semibold uppercase tracking-wider text-[color:var(--p-muted-2)]">{d}</div>)}
        </div>
        <div className="space-y-2">
          {weeks.map((row) => {
            const bandSpans = row.all_day_spans || { spans: [], lane_count: 0 };
            return (
              <div key={row.week_start} className="space-y-1">
                {/* All-day / multi-day event bars spanning day cells */}
                {bandSpans.spans.length > 0 && (
                  <div className={GRID_COLS}>
                    <div aria-hidden="true" />
                    <div className="[grid-column:2/-1]">
                      <div
                        data-testid="planner-month-allday-band"
                        style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', columnGap: '0.5rem', rowGap: '3px', gridTemplateRows: `repeat(${bandSpans.lane_count || 1},minmax(0,auto))` }}
                      >
                        {bandSpans.spans.map((s) => (
                          <button
                            key={`${s.id}-${s.start}`}
                            type="button"
                            onClick={() => onOpenDate(row.days[s.col_start]?.date)}
                            data-testid="planner-month-allday-bar"
                            title={s.title}
                            style={{ gridColumn: `${s.col_start + 1} / ${s.col_end + 2}`, gridRow: s.lane + 1 }}
                            className={`n-focus flex items-center gap-1 overflow-hidden border border-[color:var(--p-accent-soft)] bg-[color:var(--p-accent-bg)] px-1.5 py-0.5 text-left text-[10.5px] font-semibold leading-tight text-[color:var(--p-accent)] transition-colors hover:bg-[color:var(--p-accent)] hover:text-white ${s.continues_before ? 'rounded-l-none border-l-0' : 'rounded-l-md'} ${s.continues_after ? 'rounded-r-none border-r-0' : 'rounded-r-md'}`}
                          >
                            {s.continues_before && <span aria-hidden="true" className="shrink-0">&lsaquo;</span>}
                            {s.recurring && <Repeat size={9} className="shrink-0" />}
                            <span className="min-w-0 flex-1 truncate">{s.title}</span>
                            {s.continues_after && <span aria-hidden="true" className="shrink-0">&rsaquo;</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className={GRID_COLS}>
                  {/* Week-number gutter: jumps to the Weekly view for this week */}
                  <button
                    type="button"
                    onClick={() => onOpenWeek && onOpenWeek(row.week_start)}
                    data-testid="planner-month-week-number"
                    title={`Go to week ${row.week_no}`}
                    aria-label={`Open weekly planner for week ${row.week_no}`}
                    className="group n-focus flex flex-col items-center justify-center rounded-[10px] border border-transparent text-[color:var(--p-muted-2)] transition-colors hover:border-[color:var(--p-accent-soft)] hover:bg-[color:var(--p-accent-bg)]/50 hover:text-[color:var(--p-accent)]"
                  >
                    <span className="text-[8.5px] font-semibold uppercase leading-none tracking-wide opacity-70">W</span>
                    <span className="p-tnum text-[13px] font-bold leading-tight">{row.week_no}</span>
                  </button>
                  {row.days.map((cell) => (
                    <button key={cell.date} type="button" onClick={() => onOpenDate(cell.date)}
                      data-testid={cell.is_today ? 'planner-month-day-cell-today' : 'planner-month-day-cell'}
                      className={`group flex min-h-[72px] flex-col rounded-[12px] border p-2 text-left transition-colors hover:bg-[color:var(--p-accent-bg)]/40 sm:min-h-[92px] sm:p-2.5 ${
                        cell.is_today ? 'border-[color:var(--p-accent-soft)] ring-1 ring-[color:var(--p-ring)]/40' : 'border-[color:var(--p-border)]'
                      } ${cell.in_month ? 'bg-[color:var(--p-card)]' : 'bg-[color:var(--p-card-2)]/50'}`}>
                      <span className={`p-tnum text-[12px] font-semibold ${cell.is_today ? 'text-[color:var(--p-accent)]' : (cell.in_month ? 'text-[color:var(--p-text)]' : 'text-[color:var(--p-muted-2)]')}`}>{cell.day}</span>
                      <span className="mt-auto flex flex-wrap items-center gap-1.5 pt-1.5" data-testid="planner-month-day-indicators">
                        {cell.counts?.tasks > 0 && <Dot cls="bg-[color:var(--p-text-soft)]" n={cell.counts.tasks} />}
                        {cell.counts?.events > 0 && <Dot cls="bg-[color:var(--p-accent)]" n={cell.counts.events} />}
                        {cell.counts?.meetings > 0 && <Dot cls="bg-[color:var(--p-accent-2)]" n={cell.counts.meetings} />}
                        {cell.counts?.goals > 0 && <Dot cls="bg-[color:var(--p-success)]" n={cell.counts.goals} />}
                        {cell.counts?.notes > 0 && <Dot cls="bg-[color:var(--p-muted)]" n={cell.counts.notes} />}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-[color:var(--p-line)] pt-3">
          {LEGEND.map((l) => (
            <span key={l.key} className="flex items-center gap-1.5 text-[11px] text-[color:var(--p-muted)]"><span className={`h-1.5 w-1.5 rounded-full ${l.cls}`} />{l.label}</span>
          ))}
          <span className="ml-auto text-[10.5px] text-[color:var(--p-muted-2)]">Tip: click a week number to open that week</span>
        </div>
      </div>
    </div>
  );
}
